/**
 * textBounds — bounding a name WITHOUT cutting a character in half
 * (2026-09-16, Miles: "wasn't able to add special characters when editing the
 * category for some reason").
 *
 * JavaScript's `String.prototype.slice` and the `maxLength` attribute both count
 * UTF-16 code units, and every emoji — plus every character outside the Basic
 * Multilingual Plane — is TWO of them. A bound that lands between the pair
 * leaves a lone surrogate: a string that is valid JavaScript and invalid UTF-8,
 * which Firestore rejects on write. The name then fails to save with nothing
 * shown to the person who typed it.
 *
 * So every human-facing name in the thread rail is bounded by CODE POINTS. The
 * `maxLength` attribute stays on the input as the browser-side ceiling behind
 * it (at twice the bound, the most units a code point can take), so the field
 * can never be typed into a shape this cannot represent.
 *
 * Not graphemes: a family emoji or a flag is several code points joined by
 * zero-width joiners and bounding between them changes which emoji it is. That
 * needs `Intl.Segmenter`, whose availability this codebase does not assume;
 * a code-point bound is correct UTF-8 in every case and is what the storage
 * layer actually requires. Where a bound would split a joined sequence the
 * result is still a valid string — just a shorter emoji.
 */

/** How many CODE POINTS a string holds (an emoji counts once, not twice). */
export function countCodePoints(value: string): number {
  let count = 0;
  // eslint-disable-next-line no-unused-vars
  for (const _ of value) count += 1;
  return count;
}

/** The first `max` CODE POINTS of a string; never returns a lone surrogate. */
export function boundByCodePoints(value: string, max: number): string {
  if (max <= 0) return '';
  // Fast path: no astral characters ⇒ units and code points agree.
  if (value.length <= max) return value;
  let out = '';
  let count = 0;
  for (const ch of value) {
    if (count >= max) break;
    out += ch;
    count += 1;
  }
  return out;
}

/**
 * The `maxLength` an input carries behind a code-point bound: the most UTF-16
 * units `max` code points can occupy, so the browser's own cap never truncates
 * a name the bound would have kept.
 */
export function maxLengthForCodePoints(max: number): number {
  return Math.max(0, max) * 2;
}
